import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceEditModal({ open, onOpenChange, invoice, onSuccess }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client: "",
    sous_client: "",
    type: "",
    date: "",
    echeance: "",
    total_ht: "",
    total_tva: "",
    total_ttc: "",
    notes: ""
  });

  useEffect(() => {
    if (invoice) {
      setFormData({
        client: invoice.client || "",
        sous_client: invoice.sous_client || "",
        type: invoice.type || "",
        date: invoice.date || "",
        echeance: invoice.echeance || "",
        total_ht: invoice.total_ht?.toString() || "",
        total_tva: invoice.total_tva?.toString() || "",
        total_ttc: invoice.total_ttc?.toString() || "",
        notes: invoice.notes || ""
      });
    }
  }, [invoice]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate TTC when HT or TVA changes
    if (field === "total_ht" || field === "total_tva") {
      const ht = field === "total_ht" ? parseFloat(value) || 0 : parseFloat(formData.total_ht) || 0;
      const tva = field === "total_tva" ? parseFloat(value) || 0 : parseFloat(formData.total_tva) || 0;
      setFormData(prev => ({ ...prev, total_ttc: (ht + tva).toFixed(2) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoice?.numero) return;

    try {
      setLoading(true);
      
      // Send empty string for cleared optional text fields so backend can set them to null
      const payload = {
        client: formData.client || null,
        sous_client: formData.sous_client,  // Send empty string to clear, backend will convert to null
        type: formData.type || null,
        date: formData.date || null,
        echeance: formData.echeance || null,
        total_ht: formData.total_ht ? parseFloat(formData.total_ht) : null,
        total_tva: formData.total_tva ? parseFloat(formData.total_tva) : null,
        total_ttc: formData.total_ttc ? parseFloat(formData.total_ttc) : null,
        notes: formData.notes  // Send empty string to clear, backend will convert to null
      };

      await axios.put(
        `${API}/invoices/${encodeURIComponent(invoice.numero)}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Facture mise à jour avec succès");
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Modifier la facture {invoice.numero}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client et Sous-client */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client">Client principal</Label>
                <Input
                  id="client"
                  value={formData.client}
                  onChange={(e) => handleChange("client", e.target.value)}
                  placeholder="Nom du client"
                  data-testid="invoice-client-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sous_client">Sous-client</Label>
                <Input
                  id="sous_client"
                  value={formData.sous_client}
                  onChange={(e) => handleChange("sous_client", e.target.value)}
                  placeholder="Sous-client (optionnel)"
                  data-testid="invoice-sous-client-input"
                />
              </div>
            </div>
          </div>

          {/* Type et Dates */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Informations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => handleChange("type", v)}
                >
                  <SelectTrigger data-testid="invoice-type-select">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Facture">Facture</SelectItem>
                    <SelectItem value="Avoir">Avoir</SelectItem>
                    <SelectItem value="Acompte">Acompte</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  data-testid="invoice-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="echeance">Échéance</Label>
                <Input
                  id="echeance"
                  type="date"
                  value={formData.echeance}
                  onChange={(e) => handleChange("echeance", e.target.value)}
                  data-testid="invoice-echeance-input"
                />
              </div>
            </div>
          </div>

          {/* Montants */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Montants
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_ht">Total HT (€)</Label>
                <Input
                  id="total_ht"
                  type="number"
                  step="0.01"
                  value={formData.total_ht}
                  onChange={(e) => handleChange("total_ht", e.target.value)}
                  placeholder="0.00"
                  data-testid="invoice-ht-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_tva">TVA (€)</Label>
                <Input
                  id="total_tva"
                  type="number"
                  step="0.01"
                  value={formData.total_tva}
                  onChange={(e) => handleChange("total_tva", e.target.value)}
                  placeholder="0.00"
                  data-testid="invoice-tva-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_ttc">Total TTC (€)</Label>
                <Input
                  id="total_ttc"
                  type="number"
                  step="0.01"
                  value={formData.total_ttc}
                  onChange={(e) => handleChange("total_ttc", e.target.value)}
                  placeholder="0.00"
                  className="font-semibold"
                  data-testid="invoice-ttc-input"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
              Notes
            </h3>
            <div className="space-y-2">
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Notes ou commentaires sur cette facture..."
                rows={3}
                data-testid="invoice-notes-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button type="submit" disabled={loading} data-testid="invoice-save-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
